import moment from "moment-timezone";
import AddButton from "src/components/buttons/AddButton";
import EditButton from "src/components/buttons/EditButton";
import LinkButton from "src/components/buttons/LinkButton";
import { OfferingDetail } from "src/components/items/OfferingDetail";
import OfferingModel from "src/models/OfferingModel";
import { useSession } from "src/stores/Session";
import { useStore } from "src/stores/Store";
import { dateFormatLong } from "src/utils/constants";
import { modals } from "src/utils/modals";
import { MembershipStatus } from "types/code-generator";
import { BorderButton } from "../buttons/BorderButton";
import { Tag } from "../forms/Tag";
import { AddStudentToPrivateSerieModal } from "../modals/AddStudentToPrivateSerieModal";
import { OfferingDetails } from "./OfferingDetails";
import { ItemContent } from "./item";
import ItemCard from "./item/ItemCard";

interface Props {
  offering: OfferingModel;
  isPreview?: boolean;
  isDetail?: boolean;
  onExpand?: () => void;
  hideAddButton?: boolean;
  loading?: boolean;
  disableNewParticipants?: boolean;
}

export default function PrivateOffering(props: Props) {
  const session = useSession();
  const store = useStore();

  function selectOffering() {
    if (props.isPreview) {
      return;
    }

    modals.show(AddStudentToPrivateSerieModal, {
      offering: props.offering,
      disableNewParticipants: props.disableNewParticipants,
    });
  }

  function removeSeries(seriesId) {
    session.removeRegistration(seriesId);
  }

  function selectSeries(series) {
    const selectedSerie = session.privateSeries.find((s) => s.id === series.id);

    modals.show(AddStudentToPrivateSerieModal, {
      privateSerie: selectedSerie,
      offering: props.offering,
      disableNewParticipants: props.disableNewParticipants,
    });
  }

  function showStudentsTags() {
    const series = session.privateSeries.filter(
      (c) => c.offering && c.offering.id === props.offering.id
    );

    if (series.length === 0) {
      return null;
    }

    return (
      <div className="mt-4 flex gap-4">
        {series.map((s) => {
          return (
            <Tag key={s.id} closable={true} onClose={() => removeSeries(s.id)}>
              {s.roster.length} students, {s.numberOfLessons} lessons{" "}
              <EditButton onClick={() => selectSeries(s)}>Edit</EditButton>
            </Tag>
          );
        })}
      </div>
    );
  }

  let offeringDetail;
  let addButtonText = `Add ${props.offering.type}`;
  let registrationDisabled = false;

  let registrationOpen =
    props.offering.registrationOpen && moment(props.offering.registrationOpen);
  const registrationClose =
    props.offering.registrationClose &&
    moment(props.offering.registrationClose);
  const today = moment();
  let membersOnly = !props.offering.allowNonMembers;

  let userMemberships = store.user?.memberships || [];

  if (store.user?.primaryAccount) {
    userMemberships = store.user.primaryAccount.memberships || [];
  }

  let isMember = Boolean(
    userMemberships.find(
      (m) =>
        props.offering.membershipOfferings.find(
          (mo) => mo.id === m.offeringId
        ) && m.status === MembershipStatus.Active
    ) ||
      session.memberships.find(
        (sm) =>
          sm.offering.autoEnroll &&
          props.offering.membershipOfferings.find(
            (mo) => mo.id === sm.offering.id
          )
      )
  );

  if (props.isDetail) {
    offeringDetail = (
      <OfferingDetail offering={props.offering} isDetail={props.isDetail} />
    );
  }

  if (registrationOpen && today.isBefore(registrationOpen)) {
    addButtonText = `Opens ${registrationOpen.format(dateFormatLong)}`;
    registrationDisabled = true;
  }

  if (
    (registrationClose && today.isAfter(registrationClose)) ||
    !registrationOpen
  ) {
    addButtonText = `Registration Closed`;
    registrationDisabled = true;
  }

  if (membersOnly && !isMember) {
    registrationDisabled = true;
  }

  let addButton = (
    <BorderButton
      type="submit"
      onClick={selectOffering}
      isPreview={props.isPreview}
      disabled={registrationDisabled}
    >
      {addButtonText}
    </BorderButton>
  );

  if (store.isAdminArea && !props.isPreview) {
    addButton = (
      <AddButton onClick={selectOffering}>
        Add {props.offering.participantType}
      </AddButton>
    );
  }

  if (props.hideAddButton) {
    addButton = null;
  }

  return (
    <ItemCard loading={props.loading}>
      <ItemContent
        // to={
        //   props.offering.id
        //     ? `${store.isAdminArea ? "/admin" : ""}/find/${props.offering.id}`
        //     : undefined
        // }
        isPreview={props.isPreview}
      >
        <div className="mb-2">
          <OfferingDetails
            offering={props.offering}
            showPrice
            isPreview={props.isPreview}
            linkToOffering
          />
        </div>

        {props.offering.instructor && (
          <div className="mb-4 text-sm">
            Instructor • {props.offering.instructor.firstName}{" "}
            {props.offering.instructor.lastName}
          </div>
        )}
      </ItemContent>
      {addButton}

      {membersOnly && (
        <div className="pt-2 text-sm text-empty">
          This {props.offering.type.toLowerCase()} is available to select
          members.{" "}
          {props.isDetail ? (
            <>See details below.</>
          ) : (
            <LinkButton to={`/find/${props.offering.id}`}>
              <strong>View details</strong>
            </LinkButton>
          )}
        </div>
      )}

      {!props.isPreview && showStudentsTags()}
      {offeringDetail}
    </ItemCard>
  );
}
